import React, { useEffect, useState } from "react";
import { Avatar, Empty, Spin, Select, message } from "antd";
import { useAuth } from "../context/AuthContext";
import { collection, query, where, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

export default function MonCompte() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [contactInfo, setContactInfo] = useState(null);
  const [activeMembership, setActiveMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({ Status: "", Section: "", Lycee_Uni: "" });
  const [dropdowns, setDropdowns] = useState({ status: [], sections: [], etablissements: [] });
  const [dark, setDark] = useState(() => localStorage.getItem("ws-theme") === "dark");

  const toggleTheme = () => {
    setDark(d => {
      localStorage.setItem("ws-theme", !d ? "dark" : "light");
      return !d;
    });
  };

  const t = dark ? theme.dark : theme.light;

  useEffect(() => {
    const fetchData = async () => {
      if (!user?.uid) return;
      try {
        const contactSnap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
        if (!contactSnap.empty) {
          const data = contactSnap.docs[0].data();
          setContactInfo(data);
          setEditing({ Status: data.Status || "", Section: data.Section || "", Lycee_Uni: data.Lycee_Uni || "" });
        }

        const memberSnap = await getDocs(query(collection(db, "members"), where("FirebaseId", "==", user.uid)));
        const now = new Date();
        const active = memberSnap.docs
          .map(d => d.data())
          .filter(m => m.End && m.End.toDate() > now)
          .sort((a, b) => b.Start.toDate() - a.Start.toDate());
        if (active.length > 0) setActiveMembership(active[0]);

        const adminSnap = await getDocs(query(collection(db, "administration"), where("__name__", "==", "Etudiant")));
        if (!adminSnap.empty) {
          const d = adminSnap.docs[0].data();
          setDropdowns({ status: d.status || [], sections: d.sections || [], etablissements: d.etablissements || [] });
        }
      } catch (err) {
        console.error("Erreur chargement compte:", err);
      }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const handleSave = async (field, value) => {
    if (!user?.uid) return;
    try {
      const snap = await getDocs(query(collection(db, "contact"), where("FirebaseId", "==", user.uid)));
      if (!snap.empty) {
        await updateDoc(snap.docs[0].ref, { [field]: value });
        setContactInfo(prev => ({ ...prev, [field]: value }));
        setEditing(prev => ({ ...prev, [field]: value }));
        message.success(`${field} mis à jour.`);
      }
    } catch {
      message.error("Erreur lors de la mise à jour.");
    }
  };

  if (loading) return (
    <div style={{ background: t.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <Spin size="large" />
    </div>
  );

  return (
    <div style={{ background: t.bg, minHeight: "100vh" }}>
      <div style={{ maxWidth: 900, margin: "0 auto", paddingBottom: 40 }}>

        {/* Topbar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 18px 0", flexWrap: "wrap", gap: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <img src="/logo.PNG" alt="WeStudy" style={{ height: 40, objectFit: "contain" }} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600, color: t.text1 }}>Mon Compte</div>
              <div style={{ fontSize: 11, color: t.text2 }}>Profil & paramètres</div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button onClick={toggleTheme} style={{ ...s.toggleTrack, background: t.bg3, border: `1px solid ${t.border}` }}>
              <div style={{ ...s.toggleKnob, background: t.blue, transform: dark ? "translateX(16px)" : "translateX(0)" }} />
            </button>
            <button onClick={() => navigate("/")} style={{ ...s.outlineBtn, background: t.bg3, border: `1px solid ${t.border}`, color: t.text2 }}>
              ← Retour
            </button>
          </div>
        </div>

        {!contactInfo ? (
          <div style={{ padding: 60, textAlign: "center" }}>
            <Empty description={<span style={{ color: t.text2 }}>Aucune information trouvée</span>} />
          </div>
        ) : (
          <>
            {/* Hero card */}
            <div style={{ padding: "20px 18px 0" }}>
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: "24px 20px", display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
                <Avatar
                  size={72}
                  src={contactInfo.Pic}
                  style={{ border: `2px solid ${t.blue}`, flexShrink: 0 }}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 18, fontWeight: 600, color: t.text1 }}>{contactInfo.fullname}</div>
                  <div style={{ fontSize: 13, color: t.text2, marginTop: 2 }}>✉️ {contactInfo.email}</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10 }}>
                    {contactInfo.BlackList && (
                      <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 20, background: t.redBg, color: t.red }}>🚫 Blacklisté</span>
                    )}
                    <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 20, background: t.blueBg, color: t.blueText }}>
                      🎯 {contactInfo.Credit ?? 0} dt de crédit
                    </span>
                    <span style={{ fontSize: 11, padding: "2px 10px", borderRadius: 20, background: t.amberBg, color: t.amberText }}>
                      🏅 {contactInfo.Loyalty ?? 0} pts fidélité
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Two-column grid */}
            <ResponsiveGrid>

              {/* Personal info card */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18 }}>
                <SectionLabel label="Infos personnelles" t={t} />

                <InfoRow label="📅 Date de naissance" value={contactInfo.DOB} t={t} />
                <InfoRow label="🪪 CIN" value={contactInfo.CIN} t={t} />
                <InfoRow label="📖 Livre emprunté" value={contactInfo.borrowed_book || "Aucun"} t={t} />

                <div style={{ height: 1, background: t.border, margin: "12px 0" }} />

                <EditableField
                  label="🎓 Statut"
                  value={editing.Status}
                  options={dropdowns.status}
                  onChange={v => handleSave("Status", v)}
                  t={t}
                />
                <EditableField
                  label="🧪 Section"
                  value={editing.Section}
                  options={dropdowns.sections}
                  onChange={v => handleSave("Section", v)}
                  t={t}
                />
                <EditableField
                  label="🏫 Établissement"
                  value={editing.Lycee_Uni}
                  options={dropdowns.etablissements}
                  onChange={v => handleSave("Lycee_Uni", v)}
                  showSearch
                  t={t}
                />
              </div>

              {/* Membership card */}
              <div style={{ background: t.cardBg, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18 }}>
                <SectionLabel label="Abonnement actif" t={t} />

                {activeMembership ? (
                  <>
                    {/* Active badge */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: t.green }} />
                      <span style={{ fontSize: 12, color: t.green, fontWeight: 500 }}>Actif</span>
                    </div>

                    <InfoRow label="🕒 Heures / jour" value={`${activeMembership.Hours}h`} t={t} />
                    <InfoRow label="📆 Durée" value={`${activeMembership.Month_Week} jours`} t={t} />
                    <InfoRow label="🪙 Montant" value={`${activeMembership.Montant} dt`} t={t} />
                    <InfoRow label="💳 Paiement" value={activeMembership.methodofpayment || "Non spécifié"} t={t} />

                    <div style={{ height: 1, background: t.border, margin: "12px 0" }} />

                    <InfoRow label="⌚ Début" value={dayjs(activeMembership.Start.toDate()).format("DD/MM/YYYY")} t={t} />
                    <InfoRow label="🏁 Fin" value={dayjs(activeMembership.End.toDate()).format("DD/MM/YYYY")} t={t} />

                    {/* Progress bar */}
                    <div style={{ marginTop: 14 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: t.text3 }}>Progression</span>
                        <span style={{ fontSize: 11, color: t.text3 }}>
                          {Math.round(
                            ((new Date() - activeMembership.Start.toDate()) /
                              (activeMembership.End.toDate() - activeMembership.Start.toDate())) * 100
                          )}%
                        </span>
                      </div>
                      <div style={{ height: 4, borderRadius: 4, background: t.bg3, overflow: "hidden" }}>
                        <div style={{
                          height: "100%", borderRadius: 4, background: t.blue,
                          width: `${Math.min(100, Math.round(
                            ((new Date() - activeMembership.Start.toDate()) /
                              (activeMembership.End.toDate() - activeMembership.Start.toDate())) * 100
                          ))}%`
                        }} />
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{ padding: "30px 0", textAlign: "center" }}>
                    <div style={{ fontSize: 30, marginBottom: 8 }}>💳</div>
                    <div style={{ fontSize: 13, color: t.text2 }}>Aucun abonnement actif</div>
                    <button
                      onClick={() => navigate("/payment")}
                      style={{ ...s.primaryBtn, background: t.blue, marginTop: 14, padding: "8px 20px" }}
                    >
                      Souscrire un abonnement
                    </button>
                  </div>
                )}
              </div>

            </ResponsiveGrid>
          </>
        )}
      </div>
    </div>
  );
}

function SectionLabel({ label, t }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 500, color: t.text3, letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 14 }}>
      {label}
    </div>
  );
}

function InfoRow({ label, value, t }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${t.border}` }}>
      <span style={{ fontSize: 12, color: t.text2 }}>{label}</span>
      <span style={{ fontSize: 12, fontWeight: 500, color: t.text1 }}>{value ?? "—"}</span>
    </div>
  );
}

function EditableField({ label, value, options, onChange, showSearch = false, t }) {
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ fontSize: 12, color: t.text2, marginBottom: 5 }}>{label}</div>
      <Select
        value={value || undefined}
        onChange={onChange}
        placeholder="Sélectionner"
        showSearch={showSearch}
        style={{ width: "100%" }}
        options={options.map(o => ({ label: o, value: o }))}
      />
    </div>
  );
}

function ResponsiveGrid({ children }) {
  const [cols, setCols] = useState(1);
  useEffect(() => {
    const update = () => setCols(window.innerWidth >= 768 ? 2 : 1);
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0,1fr))`, gap: 10, padding: "14px 18px 0" }}>
      {children}
    </div>
  );
}

const theme = {
  light: {
    bg: "#f8fafc", bg3: "#f1f5f9", border: "#e2e8f0",
    text1: "#0f172a", text2: "#64748b", text3: "#94a3b8",
    blue: "#2563eb", blueBg: "#eff6ff", blueText: "#1d4ed8",
    green: "#16a34a", greenBg: "#f0fdf4",
    amber: "#d97706", amberBg: "#fffbeb", amberText: "#b45309",
    red: "#dc2626", redBg: "#fef2f2",
    cardBg: "#ffffff",
  },
  dark: {
    bg: "#0f172a", bg3: "#1a2740", border: "#273549",
    text1: "#f1f5f9", text2: "#94a3b8", text3: "#475569",
    blue: "#378ADD", blueBg: "#1e3a5f", blueText: "#85B7EB",
    green: "#4ade80", greenBg: "#052e16",
    amber: "#fbbf24", amberBg: "#2a1e07", amberText: "#fcd34d",
    red: "#f87171", redBg: "#2d1515",
    cardBg: "#1e293b",
  },
};

const s = {
  toggleTrack: { width: 36, height: 20, borderRadius: 20, cursor: "pointer", position: "relative", flexShrink: 0 },
  toggleKnob: { width: 14, height: 14, borderRadius: "50%", position: "absolute", top: 2, left: 2, transition: "transform 0.2s" },
  outlineBtn: { fontSize: 12, borderRadius: 8, padding: "6px 10px", cursor: "pointer" },
  primaryBtn: { fontSize: 13, fontWeight: 600, border: "none", borderRadius: 10, color: "#fff", cursor: "pointer" },
};